import { queryOptions, useQuery } from '@tanstack/react-query';

import { GET_CREDIT_ALLOWANCE, type CreditAllowanceResponse } from '../../contracts';
import { getCreditAllowance } from '../api/credit-allowance';
import { getCommerceDataConfig } from '../config';

export const CREDIT_ALLOWANCE_STALE_TIME_MS = 5 * 60 * 1000;

/** Signup provisions allowance async; first GET is often null. Poll while null, capped. API has no pending signal. */
export const CREDIT_ALLOWANCE_PROVISIONING_POLL_INTERVAL_MS = 5 * 1000;
export const CREDIT_ALLOWANCE_PROVISIONING_POLL_MAX_FETCHES = 4;

export type UseQueryCreditAllowanceProps = {
  /** `Boolean(useQueryUser().data)`, resolved app-side. */
  hasUser: boolean;
  enabled?: boolean;
  /** App-resolved Clinician Max beta — only that cohort is provisioned. */
  pollWhileUnprovisioned?: boolean;
};

export type CreditAllowanceQueryOptions = {
  enabled: boolean;
  pollWhileUnprovisioned?: boolean;
};

/** TanStack query options for the current credit allowance balance. */
export const creditAllowanceQueryOptions = ({
  enabled,
  pollWhileUnprovisioned = false,
}: CreditAllowanceQueryOptions) =>
  queryOptions({
    queryKey: [GET_CREDIT_ALLOWANCE],
    enabled,
    staleTime: CREDIT_ALLOWANCE_STALE_TIME_MS,
    refetchInterval: ({ state }) =>
      pollWhileUnprovisioned &&
      state.data == null &&
      state.dataUpdateCount + state.fetchFailureCount <
        CREDIT_ALLOWANCE_PROVISIONING_POLL_MAX_FETCHES
        ? CREDIT_ALLOWANCE_PROVISIONING_POLL_INTERVAL_MS
        : false,
    queryFn: async (): Promise<CreditAllowanceResponse> => {
      const { data } = await getCreditAllowance();
      return data ?? null;
    },
  });

/** GET /credit-allowance — current-cycle member credit balance (may be null). */
export function useQueryCreditAllowance({
  hasUser,
  enabled = true,
  pollWhileUnprovisioned = false,
}: UseQueryCreditAllowanceProps) {
  const { useAuthToken } = getCommerceDataConfig();
  const token = useAuthToken();

  return useQuery({
    ...creditAllowanceQueryOptions({
      enabled: enabled && !!token && hasUser,
      pollWhileUnprovisioned,
    }),
  });
}

/** Peek at cached allowance without subscribing. */
export const getCurrentCreditAllowance = () =>
  getCommerceDataConfig().queryClient.getQueryData<CreditAllowanceResponse>([
    GET_CREDIT_ALLOWANCE,
  ]);

export const invalidateCreditAllowance = () =>
  getCommerceDataConfig().queryClient.invalidateQueries({
    queryKey: [GET_CREDIT_ALLOWANCE],
  });
